import { getOneReportMedia } from '@brands/services/oneReport/getOneReportMedia';
import { UploadedFile } from '@brands/store/slices/patientFormSlice';
import { getPreviewCache } from '@brands/Utils/mediaPreviewCache';
import React, { useEffect, useState } from 'react';

type Props = Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  file: UploadedFile;
  userRole?: string;
};

// Renders one intake image given either an already-cached blob URL (same
// session as the upload), a media_id (fetches a presigned GET on mount),
// or a legacy dataURL (left over from the pre-media_id path).
export const UploadedFileImage = ({ file, userRole, alt, ...imgProps }: Props): JSX.Element => {
  const [src, setSrc] = useState<string | undefined>(() =>
    (file.media_id ? getPreviewCache(file.media_id) : undefined) ?? file.dataURL,
  );

  useEffect(() => {
    if (!file.media_id) return undefined;
    const cached = getPreviewCache(file.media_id);
    if (cached) {
      setSrc(cached);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const media = await getOneReportMedia(file.media_id as string, userRole);
        if (!cancelled) setSrc(media.url);
      } catch {
        // leave src undefined; the broken <img> is preferable to crashing the page
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [file.media_id, userRole]);

  return <img {...imgProps} src={src} alt={alt ?? file.name} />;
};
